import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { map, Observable } from 'rxjs';

import { ENV_CONFIG } from '@ps/core-config';

import {
  GetAllCurrenciesResponseDto,
  UpdateCurrencyPayloadDto,
  UpdateCurrencyResponseDto,
} from './dtos/currency.dto';
import {
  OperatorSettingsResponseDto,
  PSIDUploadResponseDto,
  UpdateOperatorSettingsResponseDto,
} from './dtos/operator-settings.dto';
import { OperatorSettingsMapper } from './mappers/operator-settings.mapper';
import { CurrencyMapper } from './mappers/currency.mapper';
import { CurrencyPayloadMapper } from './mappers/currency.payload.mapper';
import { OperatorSettings } from '../models/operator-settings.model';
import { Currency, CurrencyUpdatePayload } from '../models/currency.model';

@Injectable({ providedIn: 'root' })
export class OperatorSettingsApi {
  private readonly _env = inject(ENV_CONFIG);
  private readonly _http = inject(HttpClient);

  private readonly _baseUrl = this._env.pureSpectrum.url;
  private readonly _statisticsUrl =
    this._env.pureSpectrum.surveyStatiscticsServiceUrl;
  private readonly _mapper = new OperatorSettingsMapper();
  private readonly _currencyMapper = new CurrencyMapper();
  private readonly _currencyPayloadMapper = new CurrencyPayloadMapper();

  public getAll(): Observable<OperatorSettings[]> {
    return this._http
      .get<OperatorSettingsResponseDto>(`${this._baseUrl}/operatorSettings/`)
      .pipe(map((res) => res.operator.map((dto) => this._mapper.fromDto(dto))));
  }

  public update(
    id: number,
    data: Partial<OperatorSettings>,
  ): Observable<OperatorSettings[]> {
    return this._http
      .put<UpdateOperatorSettingsResponseDto>(
        `${this._baseUrl}/operatorSettings/${id}`,
        data,
      )
      .pipe(map((res) => res.operator.map((dto) => this._mapper.fromDto(dto))));
  }

  public uploadPSIDs(formData: FormData): Observable<PSIDUploadResponseDto> {
    return this._http.post<PSIDUploadResponseDto>(
      `${this._baseUrl}/operator-settings/purescore/update`,
      formData,
    );
  }

  public uploadUnicornQuotas(file: File): Observable<unknown> {
    const formData = new FormData();
    formData.append('file', file);
    return this._http.post(
      `${this._baseUrl}/buyers/v2/unicorn/upload`,
      formData,
    );
  }

  public downloadUnicornMappings(): Observable<HttpResponse<Blob>> {
    return this._http.get(`${this._baseUrl}/buyers/v2/unicorn/download`, {
      observe: 'response',
      responseType: 'blob',
    });
  }

  public uploadCrmIdFile(file: File): Observable<unknown> {
    const formData = new FormData();
    formData.append('file', file);
    return this._http.post(`${this._baseUrl}/survey/uploadCrmIds`, formData);
  }

  public getAllCurrencies(): Observable<Currency[]> {
    return this._http
      .get<GetAllCurrenciesResponseDto>(
        `${this._baseUrl}/international_currency`,
      )
      .pipe(
        map((res) =>
          res.currencies.map((dto) => this._currencyMapper.fromDto(dto)),
        ),
      );
  }

  public updateCurrency(
    currencyShortCode: string,
    data: CurrencyUpdatePayload,
  ): Observable<UpdateCurrencyResponseDto> {
    const payload: UpdateCurrencyPayloadDto =
      this._currencyPayloadMapper.toPayload(data);
    return this._http.put<UpdateCurrencyResponseDto>(
      `${this._baseUrl}/international_currency/${currencyShortCode}`,
      payload,
    );
  }

  public uploadTrafficDensityFile(
    file: File,
    country: string,
  ): Observable<unknown> {
    const formData = new FormData();
    formData.append('file', file);
    return this._http.post(
      `${this._statisticsUrl}/traffic-density/upload/${country}`,
      formData,
    );
  }

  public downloadTrafficDensityTemplate(): Observable<HttpResponse<Blob>> {
    return this._http.get(
      `${this._statisticsUrl}/traffic-density/template-file`,
      { observe: 'response', responseType: 'blob' },
    );
  }

  public downloadTrafficDensityCurrentFile(
    country: string,
  ): Observable<HttpResponse<Blob>> {
    return this._http.get(
      `${this._statisticsUrl}/traffic-density/file/${country}`,
      { observe: 'response', responseType: 'blob' },
    );
  }
}
